import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useFunnel } from '../../contexts/FunnelContext'
import { ProgressBar } from '../../components/funnel/ProgressBar'
import { PackageCard } from '../../components/funnel/PackageCard'
import { PrimaryButton } from '../../components/funnel/PrimaryButton'
import { LoadingSpinner } from '../../components/funnel/LoadingSpinner'
import { Check, X, Calendar, FileText, Wrench, Shield, Clock, Users, Sparkles, Phone } from 'lucide-react'

const Step4Page: React.FC = () => {
  const navigate = useNavigate()
  const { state, setSelectedPackage, setEmail } = useFunnel()
  const [emailInput, setEmailInput] = useState(state.email || '')
  const [isLoading, setIsLoading] = useState(false)

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmailInput(value)
    setEmail(value)
  }

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const canProceed = state.selectedPackage && isValidEmail(emailInput)

  const handleProceed = async () => {
    if (canProceed) {
      setIsLoading(true)
      // Simulate API call or processing
      await new Promise((resolve) => setTimeout(resolve, 800))
      navigate('/winter-refresh/checkout')
    }
  }

  return (
    <div style={{ backgroundColor: 'rgb(246, 246, 241)', minHeight: '100vh', padding: '2rem 0' }}>
      <div className="mx-auto max-w-4xl px-8">
        <ProgressBar currentStep={4} totalSteps={4} />

        <h2 className="text-3xl font-semibold mb-8" style={{ color: 'rgb(15,15,15)' }}>
          Choose Your Winter Home Refresh Package
        </h2>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <PackageCard
            title="Hudson Home Refresh"
            price="$199"
            description="Perfect for busy homeowners wanting peace of mind through a one-time proactive tune-up."
            features={[
              'Home Health Check (Mini Snapshot)',
              'Choose 3 Seasonal Maintenance Tasks',
              'Mini MyHome Score™ Summary Report',
              'Bonus: $50 Future Service Credit',
            ]}
            selected={state.selectedPackage === 'refresh'}
            onClick={() => setSelectedPackage('refresh')}
          />

          <PackageCard
            title="Hudson Home Deluxe Refresh"
            price="$349"
            description="Our most comprehensive assessment for a full preventive tune-up and detailed recommendations."
            features={[
              'Everything in the Refresh Package, PLUS:',
              'Expanded MyHome Snapshot™ Inspection',
              'All 6 Seasonal Maintenance Tasks Completed',
              'Comprehensive MyHome Snapshot™ Report',
              'Bonus: $100 Membership Credit',
            ]}
            recommended
            selected={state.selectedPackage === 'deluxe'}
            onClick={() => setSelectedPackage('deluxe')}
          />
        </div>

        <div className="bg-white p-6 rounded-xl mb-8">
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'rgb(15,15,15)' }}>
            Enter your email to lock in your package and receive your $100 Gift Card offer.
          </h3>
          <input
            type="email"
            placeholder="youremail@example.com"
            value={emailInput}
            onChange={handleEmailChange}
            className="w-full px-4 py-3 border-2 rounded-lg mb-4 text-base"
            style={{
              borderColor: emailInput && !isValidEmail(emailInput) ? '#dc2626' : '#dee2e6',
            }}
          />
        </div>

        <PrimaryButton onClick={handleProceed} disabled={!canProceed || isLoading} fullWidth>
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <LoadingSpinner size="sm" />
              <span>Processing...</span>
            </div>
          ) : (
            'Proceed to Checkout'
          )}
        </PrimaryButton>

        {/* Downsell Section - Lower Priced Alternative */}
        <div className="mt-8 bg-white p-6 rounded-xl border-2" style={{ borderColor: '#dee2e6' }}>
          <div className="flex items-start gap-4">
            <Phone className="w-6 h-6 flex-shrink-0 mt-1" style={{ color: '#145F6B' }} />
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2" style={{ color: 'rgb(15,15,15)' }}>
                Not Ready to Commit? Start with a Consultation
              </h3>
              <p className="text-sm mb-3" style={{ color: 'rgb(76,76,75)' }}>
                Schedule a free 30-minute phone consultation with our Home Director to discuss your
                home's specific needs. No obligation, just expert advice.
              </p>
            <Link
              to="/services"
              className="inline-block text-sm font-semibold"
              style={{ color: '#145F6B' }}
            >
              Schedule Free Consultation →
            </Link>
            </div>
          </div>
        </div>

        {/* Visual Comparison: One-Time vs Membership */}
        <div className="mt-12 bg-white p-8 rounded-xl border-2 shadow-lg" style={{ borderColor: '#145F6B' }}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5" style={{ color: '#9B775D' }} />
              <h3 className="text-2xl font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                One-Time Service vs. Full Membership
              </h3>
            </div>
            <p className="text-base" style={{ color: 'rgb(76,76,75)' }}>
              See what you get with each option
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto mb-8">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                  <th className="text-left py-3 px-4 font-semibold" style={{ color: 'rgb(15,15,15)' }}>
                    Feature
                  </th>
                  <th className="text-center py-3 px-4 font-semibold" style={{ color: '#145F6B' }}>
                    Home Refresh
                  </th>
                  <th className="text-center py-3 px-4 font-semibold" style={{ color: '#9B775D' }}>
                    Full Membership
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Home Health Inspection', refresh: 'One-time', membership: 'Monthly' },
                  { feature: 'MyHome Score™ Report', refresh: 'One-time', membership: 'Quarterly' },
                  { feature: 'Seasonal Maintenance Tasks', refresh: '3-6 tasks', membership: 'Ongoing' },
                  { feature: '24/7 Concierge Access', refresh: 'Not included', membership: 'Included' },
                  { feature: 'Dedicated Home Director', refresh: 'Not included', membership: 'Included' },
                  { feature: 'MyHome Vault™ & Blueprint™', refresh: 'Not included', membership: 'Included' },
                  { feature: 'VIP Priority Scheduling', refresh: 'Not included', membership: 'Included' },
                  { feature: 'Emergency Response', refresh: 'Not included', membership: '24/7' },
                  { feature: 'Annual Supply Credit', refresh: 'Not included', membership: '$300/year' },
                ].map((row, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? '' : ''}
                    style={{
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: index % 2 === 0 ? 'transparent' : 'rgb(246, 246, 241)',
                    }}
                  >
                    <td className="py-3 px-4 font-medium" style={{ color: 'rgb(15,15,15)' }}>
                      {row.feature}
                    </td>
                    <td className="py-3 px-4 text-center" style={{ color: 'rgb(76,76,75)' }}>
                      {row.refresh === 'Not included' ? (
                        <span className="flex items-center justify-center gap-1">
                          <X className="w-4 h-4" style={{ color: 'rgb(220, 38, 38)' }} />
                          <span>Not included</span>
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-1">
                          <Check className="w-4 h-4" style={{ color: '#145F6B' }} />
                          <span>{row.refresh}</span>
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center font-semibold" style={{ color: '#145F6B' }}>
                      <span className="flex items-center justify-center gap-1">
                        <Check className="w-4 h-4" style={{ color: '#145F6B' }} />
                        <span>{row.membership}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Membership Benefits Visual Grid */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <div className="text-center p-4 rounded-lg border-2" style={{ borderColor: '#145F6B', backgroundColor: 'rgb(246, 246, 241)' }}>
              <Calendar className="w-8 h-8 mx-auto mb-2" style={{ color: '#145F6B' }} />
              <div className="text-lg font-bold mb-1" style={{ color: 'rgb(15,15,15)' }}>
                Monthly
              </div>
              <div className="text-xs" style={{ color: 'rgb(76,76,75)' }}>
                In-home maintenance
              </div>
            </div>
            <div className="text-center p-4 rounded-lg border-2" style={{ borderColor: '#145F6B', backgroundColor: 'rgb(246, 246, 241)' }}>
              <FileText className="w-8 h-8 mx-auto mb-2" style={{ color: '#145F6B' }} />
              <div className="text-lg font-bold mb-1" style={{ color: 'rgb(15,15,15)' }}>
                Quarterly
              </div>
              <div className="text-xs" style={{ color: 'rgb(76,76,75)' }}>
                MyHome Score™ reports
              </div>
            </div>
            <div className="text-center p-4 rounded-lg border-2" style={{ borderColor: '#145F6B', backgroundColor: 'rgb(246, 246, 241)' }}>
              <Clock className="w-8 h-8 mx-auto mb-2" style={{ color: '#145F6B' }} />
              <div className="text-lg font-bold mb-1" style={{ color: 'rgb(15,15,15)' }}>
                24/7
              </div>
              <div className="text-xs" style={{ color: 'rgb(76,76,75)' }}>
                Concierge access
              </div>
            </div>
            <div className="text-center p-4 rounded-lg border-2" style={{ borderColor: '#145F6B', backgroundColor: 'rgb(246, 246, 241)' }}>
              <Shield className="w-8 h-8 mx-auto mb-2" style={{ color: '#145F6B' }} />
              <div className="text-lg font-bold mb-1" style={{ color: 'rgb(15,15,15)' }}>
                Peace of Mind
              </div>
              <div className="text-xs" style={{ color: 'rgb(76,76,75)' }}>
                Ongoing protection
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r p-6 rounded-lg text-center" style={{ background: 'linear-gradient(135deg, #145F6B 0%, #9B775D 100%)' }}>
            <h4 className="text-xl font-semibold mb-2 text-white">
              Upgrade to Full Membership
            </h4>
            <p className="text-sm mb-4 text-white/90">
              Your $50-$100 service credit can be applied toward membership • Starting at $399/month
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/services"
              className="inline-block px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
              style={{ backgroundColor: 'white', color: '#145F6B' }}
            >
              Learn About Membership →
            </Link>
            <Link
              to="/services"
              className="inline-block px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105 border-2"
              style={{ borderColor: 'white', color: 'white' }}
            >
              View Services
            </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Step4Page

